// NeuralOrbIcon.js — icono animado v5 reutilizable (header, footer, hero default).
// Diseño: núcleo cyan pulsante + 4 nodos satélites pulsando desfasados + líneas
// neurales + anillo orbital exterior rotando con dash flow.
//
// Uso:
//   <NeuralOrbIcon size={42} />              // tamaño por px
//   <NeuralOrbIcon size={120} />             // hero
//
// Props:
//   - size:  number (px) — default 42

import React from 'react';
import styled, { keyframes } from 'styled-components';

const orbit = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const nodePulse = keyframes`
  0%, 100% { transform: scale(0.85); opacity: 0.55; }
  50%      { transform: scale(1.15); opacity: 1; }
`;
const dashFlow = keyframes`
  to { stroke-dashoffset: -32; }
`;

const Wrap = styled.div`
  position: relative;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  display: inline-block;
  flex-shrink: 0;
`;
const SvgOrbit = styled.svg`
  position: absolute;
  inset: 0;
  animation: ${orbit} 16s linear infinite;
  transform-origin: 50% 50%;
`;
const SvgCore = styled.svg`
  position: absolute;
  inset: 0;
`;
const NodeG = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${nodePulse} 2.6s ease-in-out infinite;
  animation-delay: ${p => p.$delay || '0s'};
`;
const RingCircle = styled.circle`
  animation: ${dashFlow} 2.2s linear infinite;
`;

let __ng = 0; // counter para unique IDs en defs (varios orbes en una misma página)

const NeuralOrbIcon = ({ size = 42 }) => {
  // IDs únicos por instancia para que dos NeuralOrbIcon coexistan sin colisión.
  const uid = React.useMemo(() => `nor${++__ng}`, []);
  const ringId = `${uid}-ring`;
  const linkId = `${uid}-link`;
  const coreId = `${uid}-core`;
  const glowId = `${uid}-glow`;

  return (
    <Wrap $size={size}>
      {/* Anillo orbital */}
      <SvgOrbit viewBox="0 0 100 100" fill="none">
        <defs>
          <linearGradient id={ringId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.95" />
            <stop offset="50%"  stopColor="#14b6cb" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        <RingCircle
          cx="50" cy="50" r="44"
          stroke={`url(#${ringId})`}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeDasharray="6 5"
        />
      </SvgOrbit>

      {/* Núcleo + satélites + conexiones */}
      <SvgCore viewBox="0 0 100 100" fill="none">
        <defs>
          <linearGradient id={linkId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#14b6cb" stopOpacity="0.1" />
          </linearGradient>
          <radialGradient id={coreId} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity="1" />
            <stop offset="60%"  stopColor="#22d3ee" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#14b6cb" stopOpacity="0.6" />
          </radialGradient>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.2" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        <line x1="50" y1="50" x2="22" y2="22" stroke={`url(#${linkId})`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
        <line x1="50" y1="50" x2="78" y2="22" stroke={`url(#${linkId})`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
        <line x1="50" y1="50" x2="22" y2="78" stroke={`url(#${linkId})`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
        <line x1="50" y1="50" x2="78" y2="78" stroke={`url(#${linkId})`} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />

        <NodeG $delay="0s">
          <circle cx="22" cy="22" r="3.6" fill="#22d3ee" />
        </NodeG>
        <NodeG $delay="-0.65s">
          <circle cx="78" cy="22" r="3.6" fill="#22d3ee" />
        </NodeG>
        <NodeG $delay="-1.3s">
          <circle cx="22" cy="78" r="3.6" fill="#22d3ee" />
        </NodeG>
        <NodeG $delay="-1.95s">
          <circle cx="78" cy="78" r="3.6" fill="#22d3ee" />
        </NodeG>

        <circle cx="50" cy="50" r="9" fill={`url(#${coreId})`} filter={`url(#${glowId})`}>
          <animate attributeName="r" values="7;9.5;7" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.85;1;0.85" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="50" cy="50" r="3" fill="#ffffff" />
      </SvgCore>
    </Wrap>
  );
};

export default NeuralOrbIcon;
